import { useId, useState, type ReactNode } from "react";
import { Box, Button, CardActionArea, DialogContent, DialogTitle, Fab, IconButton, Paper, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "shared/hooks/useTranslation";
import ResponsiveDialog from "shared/ui/ResponsiveDialog";

interface ControlledOpenState {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

interface ExpandableCardProps extends ControlledOpenState {
    title: string;
    children: ReactNode;
    panelId?: string;
    sx?: SxProps<Theme>;
}

export function ExpandableCard({ title, children, panelId, sx, open: controlledOpen, onOpenChange }: ExpandableCardProps) {
    const { t } = useTranslation();
    const [internalOpen, setInternalOpen] = useState(false);
    const generatedId = useId();
    const open = controlledOpen ?? internalOpen;
    const id = panelId ?? `expandable-card-${generatedId}`;
    const setOpen = (next: boolean) => (onOpenChange ? onOpenChange(next) : setInternalOpen(next));

    return (
        <Paper elevation={0} sx={{ mt: 2, overflow: "hidden", border: 1, borderColor: "divider", borderRadius: 2, ...sx }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
                <Typography variant="h6" component="h2">{title}</Typography>
                <Button onClick={() => setOpen(!open)} aria-controls={open ? id : undefined} aria-expanded={open}>
                    {open ? t("common.hideDetails") : t("common.viewDetails")}
                </Button>
            </Box>
            <AnimatePresence initial={false}>
                {open ? <motion.div key="content" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <Box id={id} sx={{ p: 2 }}>{children}</Box>
                </motion.div> : null}
            </AnimatePresence>
        </Paper>
    );
}

interface PreviewCardProps extends ControlledOpenState {
    title: string;
    summary: ReactNode;
    children: ReactNode;
    actionLabel?: string;
    panelId?: string;
    sx?: SxProps<Theme>;
}

export function PreviewCard({ title, summary, children, actionLabel, panelId, sx, open: controlledOpen, onOpenChange }: PreviewCardProps) {
    const { t } = useTranslation();
    const [internalOpen, setInternalOpen] = useState(false);
    const generatedId = useId();
    const open = controlledOpen ?? internalOpen;
    const id = panelId ?? `preview-card-${generatedId}`;
    const label = actionLabel ?? (open ? t("common.hideDetails") : t("common.viewDetails"));
    const setOpen = (next: boolean) => (onOpenChange ? onOpenChange(next) : setInternalOpen(next));

    return (
        <Paper elevation={0} sx={{ mt: 2, overflow: "hidden", border: 1, borderColor: "divider", borderRadius: 2, ...sx }}>
            <CardActionArea onClick={() => setOpen(!open)} aria-controls={open ? id : undefined} aria-expanded={open} aria-label={label} sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>{title}</Typography>
                        {summary}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 700 }}>{label}</Typography>
                    </Box>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
            </CardActionArea>
            <AnimatePresence initial={false}>
                {open ? <motion.div key="content" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <Box id={id} sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>{children}</Box>
                </motion.div> : null}
            </AnimatePresence>
        </Paper>
    );
}

interface DialogCardProps extends ControlledOpenState {
    title: string;
    triggerLabel: string;
    children: ReactNode;
    disabled?: boolean;
    onRequestClose?: () => void;
}

export function DialogCard({ title, triggerLabel, children, disabled = false, onRequestClose, open: controlledOpen, onOpenChange }: DialogCardProps) {
    const { t } = useTranslation();
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = (next: boolean) => (onOpenChange ? onOpenChange(next) : setInternalOpen(next));
    const requestClose = () => {
        if (disabled) return;
        if (onRequestClose) onRequestClose();
        else setOpen(false);
    };

    return (
        <>
            {!open ? <Fab color="primary" aria-label={triggerLabel} onClick={() => setOpen(true)} disabled={disabled} sx={{ position: "fixed", right: { xs: 16, sm: 24 }, bottom: { xs: 88, sm: 24 }, zIndex: 1200 }}>
                {triggerLabel}
            </Fab> : null}
            <ResponsiveDialog open={open} onClose={requestClose} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle sx={{ pr: 6 }}>
                    {title}
                    <IconButton aria-label={t("common.close")} onClick={requestClose} disabled={disabled} sx={{ position: "absolute", right: 8, top: 8 }} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>{children}</DialogContent>
            </ResponsiveDialog>
        </>
    );
}
